import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { registerKarlaFont } from '../../fonts/karlaFont';
import { COLORS, API_URL } from './constants';
import { formatNumber, calcPercentChange, loadImageWithDimensions } from './utils';
import { drawLogo, drawPageNumber, drawTitleUnderline, drawFooterLine } from './layout';

export const generateMonositePDF = async (report) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  registerKarlaFont(doc);
  doc.setFont('Karla', 'normal');
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;
  
  const monthNames = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 
                      'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
  
  const hasHourlyData = report.hourly_data && report.hourly_data.length > 0;
  
  let analysisText = report.energy_manager_analysis || report.ai_analysis || '';
  
  if (!analysisText || analysisText.length === 0) {
    const buildingName = report.building_name || report.client_name || 'ce site';
    const totalCurrent = Object.values(report.monthly_data?.current_year || {}).reduce((sum, m) => sum + (m?.consumption_mwh || 0), 0);
    const totalPrev = Object.values(report.monthly_data?.previous_year || {}).reduce((sum, m) => sum + (m?.consumption_mwh || 0), 0) || 
                      Object.values(report.monthly_data?.current_year || {}).reduce((sum, m) => sum + (m?.previous_year_mwh || 0), 0);
    const evolution = totalPrev > 0 ? ((totalCurrent - totalPrev) / totalPrev * 100) : 0;
    const subscribedPower = report.subscribed_power_kw || 0;
    
    let peakPower = 0;
    if (report.hourly_data && report.hourly_data.length > 0) {
      peakPower = Math.max(...report.hourly_data.map(d => d.power || 0));
    }
    
    const evolutionText = evolution < 0 
      ? `une baisse de ${Math.abs(evolution).toFixed(1)}% par rapport a l'annee precedente, ce qui est une tendance positive`
      : evolution > 0 
        ? `une hausse de ${evolution.toFixed(1)}% par rapport a l'annee precedente`
        : `une consommation stable par rapport a l'annee precedente`;
    
    analysisText = `Synthese energetique pour ${buildingName}\n\n`;
    analysisText += `La consommation totale sur la periode analysee s'eleve a ${(totalCurrent * 1000).toFixed(0)} kWh, representant ${evolutionText}.\n\n`;
    
    if (subscribedPower > 0 && peakPower > 0) {
      const utilizationRate = (peakPower / subscribedPower * 100).toFixed(1);
      analysisText += `Concernant la puissance souscrite de ${subscribedPower} kW, le pic de puissance atteint est de ${peakPower.toFixed(1)} kW, soit un taux d'utilisation de ${utilizationRate}%. `;
      
      if (peakPower < subscribedPower * 0.7) {
        analysisText += `Ce taux relativement bas suggere une possible optimisation de la puissance souscrite pour reduire les couts d'abonnement.\n\n`;
      } else if (peakPower > subscribedPower * 0.9) {
        analysisText += `Attention : la puissance atteinte est proche de la puissance souscrite. Un suivi regulier est recommande pour eviter les depassements.\n\n`;
      } else {
        analysisText += `Ce niveau d'utilisation est dans la plage optimale.\n\n`;
      }
    }
    
    analysisText += `Recommandations :\n`;
    analysisText += `- Maintenir un suivi regulier de la consommation mensuelle\n`;
    analysisText += `- Identifier les equipements les plus energivores\n`;
    analysisText += `- Envisager un audit energetique pour optimiser les consommations`;
  }
  
  const currentYear = report.report_year || new Date().getFullYear();
  const monthlyData = report.monthly_data?.current_year || {};
  const prevYearData = report.monthly_data?.previous_year || {};
  const prevYear = currentYear - 1;
  
  let lastMeasuredMonthNum = 12;
  if (report.report_date) {
    const parts = report.report_date.split('/');
    if (parts.length >= 2) {
      lastMeasuredMonthNum = parseInt(parts[1]) || 12;
    }
  }
  
  const monthNamesLower = ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 
                           'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];
  if (lastMeasuredMonthNum === 12 && report.last_month?.name) {
    const monthStr = report.last_month.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const foundIndex = monthNamesLower.findIndex(m => monthStr.startsWith(m.substring(0, 3)));
    if (foundIndex >= 0) {
      lastMeasuredMonthNum = foundIndex + 2;
      if (lastMeasuredMonthNum > 12) lastMeasuredMonthNum = 12;
    }
  }
  
  let reportMonthNum = lastMeasuredMonthNum - 1;
  if (reportMonthNum < 1) reportMonthNum = 1;
  
  let totalCurrentYear = 0;
  let totalPrevYear = 0;
  let lastMonthWithData = 0;
  
  for (let m = 1; m <= lastMeasuredMonthNum; m++) {
    const monthKey = m.toString().padStart(2, '0');
    const data = monthlyData[monthKey] || {};
    const consumption = data?.consumption_mwh || data?.measured_mwh || 0;
    
    if (consumption > 0) {
      totalCurrentYear += consumption;
      totalPrevYear += (prevYearData[monthKey]?.consumption_mwh || data?.previous_year_mwh || 0);
      lastMonthWithData = m;
    }
  }
  
  const ytdMonthName = lastMonthWithData > 0 
    ? monthNames[lastMonthWithData - 1] 
    : monthNames[lastMeasuredMonthNum - 1] || 'Fevrier';
  
  const yearChange = totalPrevYear > 0 ? ((totalCurrentYear - totalPrevYear) / totalPrevYear * 100) : 0;
  
  const reportMonthKey = reportMonthNum.toString().padStart(2, '0');
  const reportMonthData = monthlyData[reportMonthKey] || {};
  const reportMonthConsumption = reportMonthData.consumption_mwh || reportMonthData.measured_mwh || reportMonthData.estimated_mwh || 0;
  const reportMonthPrevConsumption = prevYearData[reportMonthKey]?.consumption_mwh || reportMonthData.previous_year_mwh || 0;
  
  const displayMonthName = monthNames[reportMonthNum - 1] || 'Janvier';
  const displayMonthValue = reportMonthConsumption;
  const displayMonthPrevValue = reportMonthPrevConsumption;
  const monthChange = displayMonthPrevValue > 0 ? ((displayMonthValue - displayMonthPrevValue) / displayMonthPrevValue * 100) : null;
  
  let periodText = '';
  if (report.report_month) {
    periodText = report.report_month;
  } else if (reportMonthNum > 0 && reportMonthNum <= 12) {
    periodText = `${monthNames[reportMonthNum - 1]} ${currentYear}`;
  } else {
    periodText = `Annee ${currentYear}`;
  }
  
  // Helper: draw header with logos
  const drawMonositeHeader = async () => {
    await drawLogo(doc, margin, 12, 1);
    if (report.client_logo) {
      try {
        let logoData;
        if (report.client_logo.startsWith('data:')) {
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = report.client_logo;
          });
          logoData = { base64: report.client_logo, aspectRatio: img.width / img.height || 2.5 };
        } else {
          const logoUrl = report.client_logo.startsWith('http') ? report.client_logo : `${API_URL}${report.client_logo}`;
          logoData = await loadImageWithDimensions(logoUrl);
        }
        if (logoData?.base64) {
          const maxH = 14, maxW = 45;
          let logoW, logoH;
          if (logoData.aspectRatio > maxW/maxH) { logoW = maxW; logoH = logoW / logoData.aspectRatio; }
          else { logoH = maxH; logoW = logoH * logoData.aspectRatio; }
          doc.addImage(logoData.base64, 'PNG', pageWidth - margin - logoW, 12, logoW, logoH, undefined, 'FAST');
        }
      } catch (e) { /* skip logo */ }
    }
  };
  
  // PAGE 1: Title
  await drawMonositeHeader();
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(32);
  doc.setFont('Karla', 'bold');
  doc.text('RAPPORT', margin, 65);
  doc.text('CONSOMMATION', margin, 80);
  doc.setTextColor(...COLORS.azur);
  doc.text('ENERGETIQUE', margin, 95);
  drawTitleUnderline(doc, margin, 102, 70);
  
  const buildingName = report.building_name || report.client_name || 'Site';
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(24);
  doc.setFont('Karla', 'bold');
  
  const maxNameWidth = contentWidth - 20;
  let finalFontSize = 24;
  doc.setFontSize(finalFontSize);
  let nameWidth = doc.getTextWidth(buildingName.toUpperCase());
  while (nameWidth > maxNameWidth && finalFontSize > 14) {
    finalFontSize -= 1;
    doc.setFontSize(finalFontSize);
    nameWidth = doc.getTextWidth(buildingName.toUpperCase());
  }
  
  if (nameWidth > maxNameWidth) {
    const words = buildingName.split(/[\s_-]+/);
    const midPoint = Math.ceil(words.length / 2);
    doc.setFontSize(18);
    doc.text(words.slice(0, midPoint).join(' ').toUpperCase(), margin, 130);
    doc.text(words.slice(midPoint).join(' ').toUpperCase(), margin, 145);
  } else {
    doc.text(buildingName.toUpperCase(), margin, 130);
  }
  
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(14);
  doc.setFont('Karla', 'normal');
  doc.text(periodText, margin, 155);
  doc.setFontSize(12);
  if (report.pdl) doc.text(`PDL: ${report.pdl}`, margin, 170);
  
  drawFooterLine(doc, margin, pageWidth, pageHeight);
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(8);
  const dateStr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  doc.text(`Genere le ${dateStr}`, margin, pageHeight - 12);
  
  // PAGE 2: KPIs
  doc.addPage();
  await drawMonositeHeader();
  drawPageNumber(doc, 2, pageHeight);
  
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text('CHIFFRES', margin, 55);
  const chiffresWidth = doc.getTextWidth('CHIFFRES');
  doc.setTextColor(...COLORS.azur);
  doc.text('CLES', margin + chiffresWidth + 4, 55);
  drawTitleUnderline(doc, margin, 60, 50);
  
  const boxWidth = 55, boxHeight = 50, startY = 80, gap = 8;
  
  // KPI 1: Consommation du mois
  doc.setFillColor(...COLORS.blancLunaire);
  doc.roundedRect(margin, startY, boxWidth, boxHeight, 4, 4, 'F');
  doc.setDrawColor(...COLORS.azur);
  doc.setLineWidth(1);
  doc.roundedRect(margin, startY, boxWidth, boxHeight, 4, 4, 'S');
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(8);
  doc.setFont('Karla', 'normal');
  doc.text('CONSOMMATION', margin + 5, startY + 10);
  doc.text(displayMonthName.toUpperCase(), margin + 5, startY + 17);
  doc.setTextColor(...COLORS.azur);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text(`${formatNumber(displayMonthValue)}`, margin + 5, startY + 35);
  doc.setFontSize(12);
  doc.text('MWh', margin + 5, startY + 46);
  
  // KPI 2: Cumul a date
  doc.setFillColor(...COLORS.blancLunaire);
  doc.roundedRect(margin + boxWidth + gap, startY, boxWidth, boxHeight, 4, 4, 'F');
  doc.setDrawColor(...COLORS.argent);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin + boxWidth + gap, startY, boxWidth, boxHeight, 4, 4, 'S');
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(8);
  doc.setFont('Karla', 'normal');
  doc.text('CUMUL', margin + boxWidth + gap + 5, startY + 10);
  doc.text('A DATE', margin + boxWidth + gap + 5, startY + 17);
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text(`${formatNumber(totalCurrentYear)}`, margin + boxWidth + gap + 5, startY + 35);
  doc.setFontSize(12);
  doc.text('MWh', margin + boxWidth + gap + 5, startY + 46);
  
  // KPI 3: Puissance souscrite
  doc.setFillColor(...COLORS.blancLunaire);
  doc.roundedRect(margin + 2*(boxWidth + gap), startY, boxWidth, boxHeight, 4, 4, 'F');
  doc.setDrawColor(...COLORS.argent);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin + 2*(boxWidth + gap), startY, boxWidth, boxHeight, 4, 4, 'S');
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(8);
  doc.setFont('Karla', 'normal');
  doc.text('PUISSANCE', margin + 2*(boxWidth + gap) + 5, startY + 10);
  doc.text('SOUSCRITE', margin + 2*(boxWidth + gap) + 5, startY + 17);
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text(`${report.subscribed_power_kw || '-'}`, margin + 2*(boxWidth + gap) + 5, startY + 35);
  doc.setFontSize(12);
  doc.text('kW', margin + 2*(boxWidth + gap) + 5, startY + 46);
  
  // Evolution vs N-1
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(14);
  doc.setFont('Karla', 'bold');
  doc.text('EVOLUTION VS N-1', margin, startY + 75);
  
  if (monthChange !== null && displayMonthPrevValue > 0) {
    const isGood = monthChange < 0;
    const color = isGood ? COLORS.emeraude : COLORS.ambre;
    const bgColor = isGood ? COLORS.emeraudeLight : COLORS.ambreLight;
    doc.setFillColor(...bgColor);
    doc.roundedRect(margin, startY + 82, 85, 28, 4, 4, 'F');
    doc.setDrawColor(...color);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, startY + 82, 85, 28, 4, 4, 'S');
    doc.setFillColor(...color);
    const arrowX = margin + 12, arrowY = startY + 96;
    if (isGood) doc.triangle(arrowX - 4, arrowY - 4, arrowX + 4, arrowY - 4, arrowX, arrowY + 4, 'F');
    else doc.triangle(arrowX - 4, arrowY + 4, arrowX + 4, arrowY + 4, arrowX, arrowY - 4, 'F');
    doc.setTextColor(...color);
    doc.setFontSize(20);
    doc.setFont('Karla', 'bold');
    const sign = monthChange > 0 ? '+' : '';
    doc.text(`${sign}${monthChange.toFixed(1)}%`, margin + 22, startY + 100);
    doc.setTextColor(...COLORS.grisAcier);
    doc.setFontSize(9);
    doc.setFont('Karla', 'normal');
    doc.text(`vs ${displayMonthName} ${prevYear}`, margin, startY + 118);
    doc.text(`(${formatNumber(displayMonthPrevValue)} MWh)`, margin, startY + 126);
  } else {
    doc.setTextColor(...COLORS.grisAcier);
    doc.setFontSize(10);
    doc.setFont('Karla', 'normal');
    doc.text('Donnees N-1 non disponibles', margin, startY + 95);
  }
  
  // YTD comparison
  const ytdX = margin + 95;
  if (totalCurrentYear > 0) {
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(14);
    doc.setFont('Karla', 'bold');
    doc.text('CUMUL A DATE', ytdX, startY + 75);
    if (totalPrevYear > 0) {
      const isYtdGood = yearChange < 0;
      const ytdColor = isYtdGood ? COLORS.emeraude : COLORS.ambre;
      const ytdBgColor = isYtdGood ? COLORS.emeraudeLight : COLORS.ambreLight;
      doc.setFillColor(...ytdBgColor);
      doc.roundedRect(ytdX, startY + 82, 75, 28, 4, 4, 'F');
      doc.setDrawColor(...ytdColor);
      doc.setLineWidth(0.5);
      doc.roundedRect(ytdX, startY + 82, 75, 28, 4, 4, 'S');
      doc.setFillColor(...ytdColor);
      const ax = ytdX + 12, ay = startY + 96;
      if (isYtdGood) doc.triangle(ax - 4, ay - 4, ax + 4, ay - 4, ax, ay + 4, 'F');
      else doc.triangle(ax - 4, ay + 4, ax + 4, ay + 4, ax, ay - 4, 'F');
      doc.setTextColor(...ytdColor);
      doc.setFontSize(18);
      doc.setFont('Karla', 'bold');
      const ytdSign = yearChange > 0 ? '+' : '';
      doc.text(`${ytdSign}${yearChange.toFixed(1)}%`, ytdX + 22, startY + 100);
    }
    doc.setTextColor(...COLORS.grisAcier);
    doc.setFontSize(9);
    doc.setFont('Karla', 'normal');
    doc.text(`Fin ${ytdMonthName} ${prevYear}`, ytdX, startY + 118);
    doc.text(`${formatNumber(totalPrevYear)} MWh`, ytdX, startY + 126);
  }
  drawFooterLine(doc, margin, pageWidth, pageHeight);
  
  // PAGE 3: Monthly details table
  doc.addPage();
  await drawMonositeHeader();
  drawPageNumber(doc, 3, pageHeight);
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text('DETAIL', margin, 55);
  const detailWidth = doc.getTextWidth('DETAIL');
  doc.setTextColor(...COLORS.azur);
  doc.text('MENSUEL', margin + detailWidth + 4, 55);
  drawTitleUnderline(doc, margin, 60, 60);
  
  const tableData = [];
  const measuredRows = [];
  for (let i = 1; i <= 12; i++) {
    const monthKey = i.toString().padStart(2, '0');
    const currentData = monthlyData[monthKey] || {};
    const prevData = prevYearData[monthKey] || {};
    const isMeasured = i <= lastMonthWithData;
    const measuredValue = currentData.measured_mwh || 0;
    const consumptionValue = currentData.consumption_mwh || 0;
    const currentValue = measuredValue > 0 ? measuredValue : consumptionValue;
    if (currentValue <= 0) continue;
    const prevValue = prevData.consumption_mwh || currentData.previous_year_mwh || 0;
    const currentRounded = Math.round(currentValue * 1000);
    const prevRounded = Math.round(prevValue * 1000);
    const change = prevRounded > 0 ? ((currentRounded - prevRounded) / prevRounded * 100) : null;
    const monthLabel = isMeasured ? monthNames[i - 1] : `${monthNames[i - 1]} (est.)`;
    tableData.push([
      tableData.length + 1,
      monthLabel,
      currentRounded > 0 ? `${formatNumber(currentRounded)} kWh` : '-',
      prevRounded > 0 ? `${formatNumber(prevRounded)} kWh` : '-',
      change !== null && currentRounded > 0 ? `${change > 0 ? '+' : ''}${change.toFixed(1)}%` : '-'
    ]);
    measuredRows.push(isMeasured);
  }
  
  autoTable(doc, {
    startY: 75,
    head: [['#', 'Mois', `${currentYear}`, `${prevYear}`, 'Evol.']],
    body: tableData,
    theme: 'plain',
    styles: { font: 'Karla', fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: COLORS.azur, textColor: COLORS.white, fontStyle: 'bold', fontSize: 8 },
    bodyStyles: { textColor: COLORS.anthracite },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center', textColor: COLORS.grisAcier },
      1: { cellWidth: 45 },
      2: { cellWidth: 35, halign: 'right' },
      3: { cellWidth: 35, halign: 'right', textColor: COLORS.grisAcier },
      4: { cellWidth: 20, halign: 'center' },
    },
    alternateRowStyles: { fillColor: COLORS.blancLunaire },
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      const rowIndex = data.row.index;
      const isMeasuredRow = measuredRows[rowIndex];
      if (data.column.index === 1 && data.section === 'body') {
        data.cell.styles.fontStyle = isMeasuredRow ? 'bold' : 'italic';
        data.cell.styles.textColor = isMeasuredRow ? COLORS.anthracite : COLORS.grisAcier;
      }
      if (data.column.index === 2 && data.section === 'body') {
        data.cell.styles.textColor = isMeasuredRow ? COLORS.azur : COLORS.argent;
        data.cell.styles.fontStyle = isMeasuredRow ? 'bold' : 'italic';
      }
      if (data.column.index === 3 && data.section === 'body') {
        data.cell.styles.textColor = COLORS.grisAcier;
        data.cell.styles.fontStyle = isMeasuredRow ? 'normal' : 'italic';
      }
      if (data.column.index === 4 && data.section === 'body') {
        const val = parseFloat(data.cell.raw);
        if (!isNaN(val)) {
          data.cell.styles.textColor = val < 0 ? COLORS.emeraude : COLORS.ambre;
          data.cell.styles.fontStyle = isMeasuredRow ? 'bold' : 'normal';
        }
      }
    },
  });
  
  const tableEndY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(8);
  doc.setFont('Karla', 'normal');
  doc.setFillColor(...COLORS.azur);
  doc.rect(margin, tableEndY - 3, 3, 3, 'F');
  doc.setTextColor(...COLORS.anthracite);
  doc.text('Données mesurées', margin + 5, tableEndY);
  doc.setFillColor(...COLORS.argent);
  doc.rect(margin + 55, tableEndY - 3, 3, 3, 'F');
  doc.setTextColor(...COLORS.grisAcier);
  doc.text('Données estimées', margin + 60, tableEndY);
  doc.setFillColor(...COLORS.emeraude);
  doc.rect(margin + 120, tableEndY - 3, 3, 3, 'F');
  doc.setTextColor(...COLORS.anthracite);
  doc.text('Baisse', margin + 125, tableEndY);
  doc.setFillColor(...COLORS.ambre);
  doc.rect(margin + 150, tableEndY - 3, 3, 3, 'F');
  doc.text('Hausse', margin + 155, tableEndY);
  drawFooterLine(doc, margin, pageWidth, pageHeight);
  
  // PAGE 4: Hourly load curve (optional)
  if (hasHourlyData) {
    doc.addPage();
    await drawMonositeHeader();
    drawPageNumber(doc, 4, pageHeight);
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(26);
    doc.setFont('Karla', 'bold');
    doc.text('COURBE DE', margin, 55);
    const courbeWidth = doc.getTextWidth('COURBE DE');
    doc.setTextColor(...COLORS.azur);
    doc.text('CHARGE', margin + courbeWidth + 4, 55);
    drawTitleUnderline(doc, margin, 60, 70);
    
    const loadChartY = 80, loadChartHeight = 90, loadChartWidth = contentWidth;
    const hourlyDataArr = report.hourly_data || [];
    let maxPower = 0;
    hourlyDataArr.forEach(d => { if (d.power > maxPower) maxPower = d.power; });
    const subscribedPower = report.subscribed_power_kw || 0;
    maxPower = Math.max(maxPower, subscribedPower) * 1.1;
    
    doc.setFillColor(...COLORS.blancLunaire);
    doc.roundedRect(margin, loadChartY, loadChartWidth, loadChartHeight, 3, 3, 'F');
    doc.setDrawColor(...COLORS.argent);
    doc.setLineWidth(0.2);
    for (let i = 1; i < 5; i++) {
      const y = loadChartY + (loadChartHeight * i / 5);
      doc.line(margin, y, margin + loadChartWidth, y);
    }
    
    if (subscribedPower > 0 && maxPower > 0) {
      const powerY = loadChartY + loadChartHeight - (subscribedPower / maxPower) * loadChartHeight;
      doc.setDrawColor(239, 68, 68);
      doc.setLineWidth(0.8);
      doc.setLineDashPattern([3, 2], 0);
      doc.line(margin + 5, powerY, margin + loadChartWidth - 5, powerY);
      doc.setLineDashPattern([], 0);
      doc.setFontSize(8);
      doc.setTextColor(239, 68, 68);
      doc.text(`Puissance souscrite: ${subscribedPower} kW`, margin + loadChartWidth - 5, powerY - 3, { align: 'right' });
    }
    
    if (hourlyDataArr.length > 0) {
      doc.setDrawColor(...COLORS.azur);
      doc.setLineWidth(0.5);
      let prevX = margin, prevY = loadChartY + loadChartHeight;
      const sampleRate = Math.max(1, Math.floor(hourlyDataArr.length / 200));
      for (let i = 0; i < hourlyDataArr.length; i += sampleRate) {
        const x = margin + (i / hourlyDataArr.length) * loadChartWidth;
        const power = hourlyDataArr[i].power || 0;
        const y = loadChartY + loadChartHeight - (power / maxPower) * loadChartHeight;
        if (i > 0) doc.line(prevX, prevY, x, y);
        prevX = x; prevY = y;
      }
    }
    
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.bleuPetroleDark);
    for (let i = 0; i <= 4; i++) {
      const value = Math.round(maxPower * (4 - i) / 4);
      const y = loadChartY + (loadChartHeight * i / 4) + 3;
      doc.text(`${value} kW`, margin - 2, y, { align: 'right' });
    }
    
    const legendY = loadChartY + loadChartHeight + 10;
    doc.setFillColor(...COLORS.azur);
    doc.rect(margin, legendY, 15, 2.5, 'F');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.bleuPetroleDark);
    doc.text('Puissance atteinte (kW)', margin + 18, legendY + 2);
    if (subscribedPower > 0) {
      doc.setDrawColor(239, 68, 68);
      doc.setLineWidth(0.8);
      doc.setLineDashPattern([3, 2], 0);
      doc.line(margin + 75, legendY + 1, margin + 88, legendY + 1);
      doc.setLineDashPattern([], 0);
      doc.setTextColor(239, 68, 68);
      doc.text('Puissance souscrite', margin + 91, legendY + 2);
    }
    
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(14);
    doc.setFont('Karla', 'bold');
    doc.text('PUISSANCES SOUSCRITES', margin, loadChartY + loadChartHeight + 30);
    
    const powers = report.subscribed_powers || {};
    const powerData = [];
    if (powers.base) powerData.push(['Base', `${powers.base} kW`]);
    if (powers.hcb) powerData.push(['Heures Creuses Basses (HCB)', `${powers.hcb} kW`]);
    if (powers.hch) powerData.push(['Heures Creuses Hautes (HCH)', `${powers.hch} kW`]);
    if (powers.hpb) powerData.push(['Heures Pleines Basses (HPB)', `${powers.hpb} kW`]);
    if (powers.hph) powerData.push(['Heures Pleines Hautes (HPH)', `${powers.hph} kW`]);
    if (powers.hc) powerData.push(['Heures Creuses (HC)', `${powers.hc} kW`]);
    if (powers.hp) powerData.push(['Heures Pleines (HP)', `${powers.hp} kW`]);
    if (powers.hce) powerData.push(['Heures Creuses Ete (HCE)', `${powers.hce} kW`]);
    if (powers.hpe) powerData.push(['Heures Pleines Ete (HPE)', `${powers.hpe} kW`]);
    if (powers.hcw) powerData.push(['Heures Creuses Hiver (HCW)', `${powers.hcw} kW`]);
    if (powers.hpw) powerData.push(['Heures Pleines Hiver (HPW)', `${powers.hpw} kW`]);
    if (powers.pointe) powerData.push(['Pointe', `${powers.pointe} kW`]);
    if (powerData.length === 0 && report.subscribed_power_kw) {
      powerData.push(['Puissance souscrite', `${report.subscribed_power_kw} kW`]);
    }
    if (powerData.length > 0) {
      autoTable(doc, {
        startY: loadChartY + loadChartHeight + 35,
        body: powerData,
        theme: 'plain',
        styles: { font: 'Karla' },
        bodyStyles: { fontSize: 9, textColor: COLORS.anthracite },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { halign: 'right', cellWidth: 40, fontStyle: 'bold', textColor: COLORS.azur }
        },
        margin: { left: margin, right: margin + 50 }
      });
    }
    drawFooterLine(doc, margin, pageWidth, pageHeight);
  }
  
  // PAGE: Analysis
  doc.addPage();
  await drawMonositeHeader();
  const analysisPageNum = hasHourlyData ? 5 : 4;
  drawPageNumber(doc, analysisPageNum, pageHeight);
  doc.setTextColor(...COLORS.anthracite);
  doc.setFontSize(26);
  doc.setFont('Karla', 'bold');
  doc.text('ANALYSE', margin, 55);
  const analyseWidth = doc.getTextWidth('ANALYSE');
  doc.setTextColor(...COLORS.azur);
  doc.text('ENERGY MANAGER', margin + analyseWidth + 4, 55);
  drawTitleUnderline(doc, margin, 60, 100);
  
  const cleanTextForPDF = (text) => {
    return text
      .replace(/ < /g, ' inferieur a ').replace(/ > /g, ' superieur a ')
      .replace(/<(\d)/g, 'inferieur a $1').replace(/>(\d)/g, 'superieur a $1')
      .replace(/≤/g, 'inferieur ou egal a ').replace(/≥/g, 'superieur ou egal a ')
      .replace(/–/g, '-').replace(/—/g, '-')
      .replace(/'/g, "'").replace(/'/g, "'")
      .replace(/"/g, '"').replace(/"/g, '"')
      .replace(/…/g, '...').replace(/\u00A0/g, ' ').replace(/  +/g, ' ');
  };
  
  const renderAnalysisText = (analysisStr) => {
    const availableWidth = pageWidth - (margin * 2);
    const textStartX = margin + 8;
    const maxWidth = availableWidth - 16;
    const cleanedText = cleanTextForPDF(analysisStr);
    let currentY = 82;
    const lineHeight = 4.0;
    const sectionSpacing = 8;
    const endY = pageHeight - 30;
    
    doc.setFillColor(...COLORS.blancLunaire);
    doc.roundedRect(margin, 70, availableWidth, pageHeight - 95, 4, 4, 'F');
    doc.setFillColor(...COLORS.azur);
    doc.rect(margin, 70, 4, pageHeight - 95, 'F');
    
    const lines = cleanedText.split('\n');
    for (const line of lines) {
      if (currentY > endY - 10) break;
      if (line.startsWith('## ')) {
        currentY += sectionSpacing;
        if (currentY > endY - 20) break;
        doc.setTextColor(...COLORS.azur);
        doc.setFontSize(12);
        doc.setFont('Karla', 'bold');
        doc.text(line.replace(/^##\s*/, '').replace(/[📊🔍💡🎯📈⚠️🚨🌡️🌿🍂♻️🔋🤖]/g, '').trim(), textStartX, currentY);
        currentY += 6;
      } else if (line.startsWith('### ')) {
        currentY += 4;
        if (currentY > endY - 15) break;
        doc.setTextColor(...COLORS.anthracite);
        doc.setFontSize(10);
        doc.setFont('Karla', 'bold');
        doc.text(line.replace(/^###\s*/, '').replace(/[📊🔍💡🎯📈⚠️🚨🌡️🌿🍂♻️🔋🤖]/g, '').trim(), textStartX, currentY);
        currentY += 5;
      } else if (line.trim().startsWith('- ') || /^\d+\.\s/.test(line.trim())) {
        doc.setTextColor(...COLORS.anthracite);
        doc.setFontSize(9);
        doc.setFont('Karla', 'normal');
        let content = line.trim().replace(/^- /, '• ').replace(/\*\*(.+?)\*\*/g, '$1');
        const wrapped = doc.splitTextToSize(content, maxWidth - 10);
        for (const wl of wrapped) {
          if (currentY > endY - 5) break;
          doc.text(wrapped.indexOf(wl) === 0 ? wl : '   ' + wl, textStartX + 5, currentY);
          currentY += lineHeight;
        }
      } else if (!line.trim()) {
        currentY += 2;
      } else {
        doc.setTextColor(...COLORS.anthracite);
        doc.setFontSize(9);
        doc.setFont('Karla', 'normal');
        const wrapped = doc.splitTextToSize(line.replace(/\*\*(.+?)\*\*/g, '$1'), maxWidth);
        for (const wl of wrapped) {
          if (currentY > endY - 5) break;
          doc.text(wl, textStartX, currentY);
          currentY += lineHeight;
        }
      }
    }
    return analysisPageNum;
  };
  
  let finalAnalysisPageNum = renderAnalysisText(analysisText);
  
  const cleanedFull = cleanTextForPDF(analysisText);
  const allLines = cleanedFull.split('\n');
  const estimatedLinesPerPage = Math.floor((pageHeight - 110) / 4.0);
  const totalEstimatedLines = allLines.reduce((acc, line) => {
    if (!line.trim()) return acc + 1;
    return acc + Math.ceil(line.length / 90);
  }, 0);
  
  if (totalEstimatedLines > estimatedLinesPerPage * 1.5) {
    doc.addPage();
    await drawMonositeHeader();
    finalAnalysisPageNum++;
    drawPageNumber(doc, finalAnalysisPageNum, pageHeight);
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(22);
    doc.setFont('Karla', 'bold');
    doc.text('ANALYSE', margin, 55);
    const aw2 = doc.getTextWidth('ANALYSE');
    doc.setTextColor(...COLORS.azur);
    doc.text('ENERGY MANAGER (suite)', margin + aw2 + 4, 55);
    drawTitleUnderline(doc, margin, 60, 120);
    
    const availableWidth = pageWidth - (margin * 2);
    doc.setFillColor(...COLORS.blancLunaire);
    doc.roundedRect(margin, 70, availableWidth, pageHeight - 95, 4, 4, 'F');
    doc.setFillColor(...COLORS.azur);
    doc.rect(margin, 70, 4, pageHeight - 95, 'F');
    
    let currentY = 82;
    const maxWidth = availableWidth - 16;
    const textStartX = margin + 8;
    const endY = pageHeight - 30;
    let linesRendered = 0;
    const skipLines = estimatedLinesPerPage - 5;
    
    for (const line of allLines) {
      linesRendered++;
      if (linesRendered <= skipLines) continue;
      if (currentY > endY - 5) break;
      if (line.startsWith('## ') || line.startsWith('### ')) {
        const isSection = line.startsWith('## ');
        currentY += isSection ? 8 : 4;
        doc.setTextColor(...(isSection ? COLORS.azur : COLORS.anthracite));
        doc.setFontSize(isSection ? 12 : 10);
        doc.setFont('Karla', 'bold');
        doc.text(line.replace(/^##+\s*/, '').replace(/[📊🔍💡🎯📈⚠️🚨🌡️🌿🍂♻️🔋🤖]/g, '').trim(), textStartX, currentY);
        currentY += isSection ? 6 : 5;
      } else if (line.trim().startsWith('- ') || /^\d+\.\s/.test(line.trim())) {
        doc.setTextColor(...COLORS.anthracite);
        doc.setFontSize(9);
        doc.setFont('Karla', 'normal');
        let content = line.trim().replace(/^- /, '• ').replace(/\*\*(.+?)\*\*/g, '$1');
        doc.splitTextToSize(content, maxWidth - 10).forEach((wl, idx) => {
          if (currentY <= endY - 5) {
            doc.text(idx === 0 ? wl : '   ' + wl, textStartX + 5, currentY);
            currentY += 4;
          }
        });
      } else if (line.trim()) {
        doc.setTextColor(...COLORS.anthracite);
        doc.setFontSize(9);
        doc.setFont('Karla', 'normal');
        doc.splitTextToSize(line.replace(/\*\*(.+?)\*\*/g, '$1'), maxWidth).forEach(wl => {
          if (currentY <= endY - 5) {
            doc.text(wl, textStartX, currentY);
            currentY += 4;
          }
        });
      } else {
        currentY += 2;
      }
    }
    drawFooterLine(doc, margin, pageWidth, pageHeight);
  }
  
  drawFooterLine(doc, margin, pageWidth, pageHeight);
  
  // FINAL PAGE: Closing
  doc.addPage();
  const centerX = pageWidth / 2;
  const closingLogoHeight = 22;
  const closingLogoWidth = closingLogoHeight * 2.755;
  await drawLogo(doc, centerX - (closingLogoWidth / 2), 60, 1.8);
  
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(14);
  doc.setFont('Karla', 'normal');
  doc.text('Votre partenaire en efficacite energetique', centerX, 110, { align: 'center' });
  doc.setDrawColor(...COLORS.argent);
  doc.setLineWidth(0.5);
  doc.line(centerX - 50, 120, centerX + 50, 120);
  
  const contactInfo = report.contact_info || {};
  const hasContactInfo = contactInfo.name || contactInfo.email || contactInfo.phone || contactInfo.agency;
  if (hasContactInfo) {
    doc.setTextColor(...COLORS.anthracite);
    doc.setFontSize(12);
    doc.setFont('Karla', 'bold');
    doc.text('Votre contact Place des Energies', centerX, 135, { align: 'center' });
    let yPos = 148;
    doc.setFontSize(11);
    doc.setFont('Karla', 'normal');
    doc.setTextColor(...COLORS.bleuPetroleDark);
    if (contactInfo.name) { doc.setFont('Karla', 'bold'); doc.text(contactInfo.name, centerX, yPos, { align: 'center' }); yPos += 10; doc.setFont('Karla', 'normal'); }
    if (contactInfo.title) { doc.setTextColor(...COLORS.grisAcier); doc.text(contactInfo.title, centerX, yPos, { align: 'center' }); yPos += 10; doc.setTextColor(...COLORS.bleuPetroleDark); }
    if (contactInfo.agency) { doc.text(contactInfo.agency, centerX, yPos, { align: 'center' }); yPos += 10; }
    if (contactInfo.phone) { doc.text(contactInfo.phone, centerX, yPos, { align: 'center' }); yPos += 10; }
    if (contactInfo.email) { doc.setTextColor(...COLORS.azur); doc.text(contactInfo.email, centerX, yPos, { align: 'center' }); yPos += 10; }
    if (contactInfo.address) { doc.setTextColor(...COLORS.grisAcier); doc.setFontSize(9); doc.text(contactInfo.address, centerX, yPos, { align: 'center' }); }
  } else {
    doc.setTextColor(...COLORS.bleuPetroleDark);
    doc.setFontSize(11);
    doc.text('www.placedesenergies.com', centerX, 140, { align: 'center' });
    doc.text('contact@placedesenergies.com', centerX, 152, { align: 'center' });
  }
  
  doc.setTextColor(...COLORS.azur);
  doc.setFontSize(16);
  doc.setFont('Karla', 'bold');
  doc.text('Merci de votre confiance', centerX, 220, { align: 'center' });
  doc.setTextColor(...COLORS.grisAcier);
  doc.setFontSize(9);
  doc.setFont('Karla', 'normal');
  doc.text('www.placedesenergies.com', centerX, pageHeight - 15, { align: 'center' });
  
  // Save
  const clientName = (report.building_name || report.client_name || 'rapport')
    .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  const monthName = (report.report_month || 'rapport')
    .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');
  const fileName = `rapport_${clientName}_${monthName}_${currentYear}.pdf`;
  doc.save(fileName);
  return fileName;
};
